import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from "@mui/material";
import type { TenantListItemDto } from "Infrastructure/Api/Api";
import { useCreateTenantMutation } from "Tenants/Api/Mutations/useCreateTenantMutation";
import { useUpdateTenantMutation } from "Tenants/Api/Mutations/useUpdateTenantMutation";
import { type FC, useEffect, useState } from "react";

type Props = {
	open: boolean;
	onClose: () => void;
	editData?: TenantListItemDto;
};

export const CreateTenantDialog: FC<Props> = ({ open, onClose, editData }) => {
	const [tenantCode, setTenantCode] = useState("");
	const [error, setError] = useState("");
	const createTenantMutation = useCreateTenantMutation();
	const updateTenantMutation = useUpdateTenantMutation();

	const isEditMode = !!editData;

	useEffect(() => {
		if (editData) {
			setTenantCode(editData.code);
		} else {
			setTenantCode("");
		}
		setError("");
	}, [editData, open]);

	const handleClose = () => {
		setTenantCode("");
		setError("");
		onClose();
	};

	const handleSubmit = async () => {
		if (!tenantCode.trim()) {
			setError("Tenant code is required");
			return;
		}

		try {
			let result;
			if (isEditMode && editData) {
				result = await updateTenantMutation.mutateAsync({
					id: editData.tenantID,
					command: { code: tenantCode.trim() },
				});
			} else {
				result = await createTenantMutation.mutateAsync({
					code: tenantCode.trim(),
				});
			}

			if (result.status === 409 && result.data?.errors) {
				// Extract error message from the errors object
				const errorMessages = Object.values(result.data.errors)
					.flat()
					.map((err: any) => err.message)
					.filter(Boolean);

				if (errorMessages.length > 0) {
					setError(errorMessages[0]);
					return;
				}
			}

			handleClose();
		} catch (err) {
			setError(
				`Failed to ${isEditMode ? "update" : "create"} tenant. Please try again.`,
			);
		}
	};

	const isLoading =
		createTenantMutation.isPending || updateTenantMutation.isPending;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				{isEditMode ? "Edit Tenant" : "Create New Tenant"}
			</DialogTitle>
			<DialogContent>
				<TextField
					autoFocus
					margin="dense"
					label="Tenant Code"
					type="text"
					fullWidth
					variant="outlined"
					value={tenantCode}
					onChange={(e) => {
						setTenantCode(e.target.value);
						setError("");
					}}
					error={!!error}
					helperText={error}
					placeholder="Enter tenant code (e.g., ACME, CLIENT01)"
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="inherit">
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					color="primary"
					disabled={isLoading}
				>
					{isLoading
						? isEditMode
							? "Updating..."
							: "Creating..."
						: isEditMode
							? "Update"
							: "Create"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
